import {
    FormControl,
    FormHelperText,
    Input,
    InputAdornment,
    InputLabel,
    MenuItem,
    MenuList,
    Paper,
    Popper,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Downshift from "downshift";
import PropTypes from "prop-types";
import { useRef } from "react";

const StyledFormHelperText = styled(FormHelperText)(({ required }) => ({
    fontStyle: "italic",
    margin: "0px",

    ...(required && {
        color: "dimgrey",
    }),
}));

const StyledFormControl = styled(FormControl)(() => ({
    marginBottom: "22px",
    width: "100%",
}));

const StyledInput = styled(Input)(({ theme }) => ({
    borderRadius: 4,
    border: "1px solid #E0E0E0",
    fontSize: 14,
    padding: "4px",
    transition: theme.transitions.create(["border-color", "box-shadow"]),
    "&:focus": {
        borderColor: "#80bdff",
        boxShadow: "0 0 0 0.2rem rgba(0,123,255,.25)",
    },
}));

const StyledMenuItemTitle = styled("span")({
    fontSize: "14px",
    margin: 0,
    padding: 0,
});

const StyledMenuItemSubtext = styled("span")({
    fontSize: "12px",
    margin: 0,
    padding: 0,
});

const StyledMenuItem = styled("div")({
    width: "100%",
    lineHeight: "1em",
});

const StyledResultType = styled("div")({
    float: "right",
    textTransform: "uppercase",
    fontSize: "13px",
    fontWeight: 600,
    color: "#9E9E9E",
});

const StyledPaper = styled(Paper)(() => ({
    maxHeight: "210px",
    overflowY: "auto",
    zIndex: 1300,
}));

const StyledContainer = styled("div")({
    flexGrow: 1,
    position: "relative",
});

const StyledFormLabel = styled(InputLabel)(({ theme }) => ({
    "&.MuiInputLabel-root": {
        "&.Mui-focused": {
            color: theme.palette.primary.main,
        },
    },
}));

function renderInput(inputProps) {
    const {
        endAdornment,
        error,
        InputProps,
        label,
        ref,
        startAdornment,
        errorText,
        helperText,
        extraFormControlStyles,
    } = inputProps;

    return (
        <StyledFormControl style={{ ...extraFormControlStyles }}>
            {label && <StyledFormLabel shrink>{label}</StyledFormLabel>}
            <StyledInput
                inputRef={ref}
                disableUnderline={true}
                autoComplete={"no"}
                style={error ? { borderColor: "red" } : {}}
                endAdornment={endAdornment && <InputAdornment position="end">{endAdornment}</InputAdornment>}
                startAdornment={startAdornment && <InputAdornment position="start">{startAdornment}</InputAdornment>}
                {...InputProps}
            />
            {error ? (
                <StyledFormHelperText error={true}>{errorText}</StyledFormHelperText>
            ) : (
                <StyledFormHelperText>{helperText}</StyledFormHelperText>
            )}
        </StyledFormControl>
    );
}

function renderSuggestion({
    suggestion,
    index,
    itemProps,
    highlightedIndex,
    selectedItem,
    key,
    display,
    displaySub,
    displayType,
}) {
    const isHighlighted = highlightedIndex === index;
    const isSelected = selectedItem ? (String(selectedItem[key]) || "").indexOf(suggestion[key]) > -1 : false;

    return (
        <MenuItem
            {...itemProps}
            key={key}
            selected={isHighlighted}
            component="div"
            className="waterloo"
            style={{
                fontWeight: isSelected ? 500 : 400,
                backgroundColor: isHighlighted ? "#F5F5F5" : "white",
                zIndex: "5000",
            }}
        >
            <StyledMenuItem>
                <StyledMenuItemTitle>{display(suggestion)}</StyledMenuItemTitle>
                <br />
                <StyledMenuItemSubtext>{displaySub && displaySub(suggestion)}</StyledMenuItemSubtext>
            </StyledMenuItem>
            <StyledResultType>{displayType && <div>{displayType(suggestion)} Display Type</div>}</StyledResultType>
        </MenuItem>
    );
}
renderSuggestion.propTypes = {
    highlightedIndex: PropTypes.number,
    index: PropTypes.number,
    itemProps: PropTypes.object,
    selectedItem: PropTypes.string,
    suggestion: PropTypes.shape({ label: PropTypes.string }).isRequired,
};

const Autocomplete = (props) => {
    const {
        id,
        error,
        label,
        options,
        onSelect,
        disabled,
        placeholder,
        suggestions,
        itemToString,
        endAdornment,
        freeFormValue,
        searchFunction,
        startAdornment,
        initialInputValue,
        errorText,
        helperText,
        extraFormControlStyles,
    } = props;

    const anchorRef = useRef(null);

    return (
        <div>
            <Downshift
                id={id}
                onSelect={onSelect}
                itemToString={itemToString}
                onInputValueChange={searchFunction}
                initialInputValue={initialInputValue}
            >
                {({
                    getRootProps,
                    isOpen,
                    inputValue,
                    getItemProps,
                    getMenuProps,
                    selectedItem,
                    getInputProps,
                    highlightedIndex,
                }) => {
                    return (
                        <StyledContainer {...getRootProps({}, { suppressRefError: true })} ref={anchorRef}>
                            {renderInput({
                                fullWidth: true,
                                extraFormControlStyles: extraFormControlStyles,
                                endAdornment: endAdornment,
                                startAdornment: startAdornment,
                                errorText: errorText,
                                helperText: helperText,
                                id: id,
                                label: label,
                                error: error,
                                InputProps: getInputProps({
                                    placeholder: placeholder,
                                    value: isOpen ? inputValue : initialInputValue,
                                    disabled: disabled,
                                }),
                            })}
                            <Popper
                                open={isOpen}
                                anchorEl={anchorRef.current}
                                placement="bottom-start"
                                modifiers={[
                                    {
                                        name: "flip",
                                        enabled: true,
                                        options: {
                                            fallbackPlacements: ["top-start"],
                                        },
                                    },
                                    {
                                        name: "preventOverflow",
                                        enabled: true,
                                        options: {
                                            boundary: "clippingParents",
                                        },
                                    },
                                ]}
                                style={{ width: anchorRef.current?.clientWidth, zIndex: 1300 }}
                            >
                                <StyledPaper square {...getMenuProps({}, { suppressRefError: true })}>
                                    <MenuList>
                                        {suggestions &&
                                            suggestions.map((suggestion, index) =>
                                                renderSuggestion({
                                                    suggestion,
                                                    index,
                                                    itemProps: getItemProps({ item: suggestion }),
                                                    highlightedIndex,
                                                    selectedItem: !freeFormValue ? null : selectedItem,
                                                    key: suggestion[options.key],
                                                    display: options.display,
                                                    displaySub: options.displaySub,
                                                    displayType: options.displayType,
                                                })
                                            )}
                                    </MenuList>
                                </StyledPaper>
                            </Popper>
                        </StyledContainer>
                    );
                }}
            </Downshift>
        </div>
    );
};

export default Autocomplete;
